import React, { forwardRef } from 'react';
import styled from 'styled-components';
import dayjs from 'utils/dayjs';
import Toggle from 'react-toggle';
import Input from 'components/forms/Input';
import Button from 'components/forms/Button';
import TextArea from 'components/forms/Input/TextArea';
import InputLabel from '../common/InputLabel';
import LabelSelectRow from './LabelSelectRow';
import FormRow from './FormRow';
import Select from 'react-select';
import DatePicker from '../common/FormDatePickerInput';
import OutputLabel from '../common/OutputLabel';

const options = [
    { value: 'brand', label: 'Brand.com' },
    { value: 'other', label: 'Other' },
];

const CustomInput = forwardRef(({ value, onClick }, ref) => (
    <Button className="pkgCalc-form-picker-button" onClick={onClick} ref={ref}>
        {value}
    </Button>
));

const Title = styled.h2`
    font-family: ${({ theme }) => theme.font.title};
    font-size: 1.9em;
    color: ${({ theme }) => theme.colour.dark};
    text-align: left;
    margin-top: 0.5em;
    margin-bottom: 0.5em;
`;

const rowStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    marginBottom: '1em',
};

const Proposals = ({ formData, currency, onDateInputChange, onInputChange, onSelectChange, selectStyles }) => {
    const commissionApplied = (event) => {
        onInputChange({
            target: {
                name: 'commissionApplied',
                value: event.target.checked,
            },
        });
        if (!event.target.checked) {
            const resetValues = {
                target: {
                    name: 'commissionPercentage',
                    value: 0,
                },
            };
            onInputChange(resetValues);
        }
    };

    const selectedCommissionOptionValue = options.find((opt) => opt.value === formData.commissionBrand);

    return (
        <div className="pkgCalc-form-proposals">
            <Title>Rate Details</Title>
            <hr style={{ marginBottom: '2em' }} />
            <FormRow style={rowStyle}>
                <InputLabel text="Title" />
                <Input
                    name="rateTitle"
                    placeholder="Rate Title"
                    onChange={onInputChange}
                    defaultValue=""
                    className="pkgCalc-form-text"
                    value={formData.rateTitle}
                />
            </FormRow>
            <FormRow style={rowStyle}>
                <InputLabel text="Description" />
                <TextArea
                    name="rateDescription"
                    placeholder="Rate Description"
                    type="textarea"
                    rows={3}
                    onChange={onInputChange}
                    className="pkgCalc-form-textarea"
                    value={formData.rateDescription}
                />
            </FormRow>
            <FormRow style={rowStyle}>
                <InputLabel text="Valid From" />
                <div className="pkgCalc-form-datepicker">
                    <DatePicker
                        onChange={(date) => onDateInputChange('validFrom', date.format('YYYY-MM-DD'))}
                        name="validFrom"
                        selected={dayjs(formData.validFrom)}
                        dateFormat="DD/MM/YYYY"
                        inputClassName="pkgCalc-form-datepicker-wrapper"
                        peekNextMonth
                        customInput={<CustomInput />}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />
                </div>
            </FormRow>
            <FormRow style={rowStyle}>
                <InputLabel text="Valid To" />
                <div className="pkgCalc-form-datepicker">
                    <DatePicker
                        onChange={(date) => onDateInputChange('validTo', date.format('YYYY-MM-DD'))}
                        name="validTo"
                        selected={dayjs(formData.validTo)}
                        dateFormat="DD/MM/YYYY"
                        peekNextMonth
                        customInput={<CustomInput />}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                    />
                </div>
            </FormRow>
            <FormRow style={rowStyle}>
                <InputLabel text="Release Date" />
                <div className="pkgCalc-form-datepicker">
                    <DatePicker
                        onChange={(date) => onDateInputChange('releaseDate', date.format('YYYY-MM-DD'))}
                        name="releaseDate"
                        selected={dayjs(formData.releaseDate)}
                        dateFormat="DD/MM/YYYY"
                        peekNextMonth
                        showMonthDropdown
                        customInput={<CustomInput />}
                        showYearDropdown
                        dropdownMode="select"
                    />
                </div>
            </FormRow>
            <Title>Price and variables</Title>
            <hr style={{ marginBottom: '2em' }} />
            <FormRow style={rowStyle}>
                <InputLabel text="Proposed Selling Rate" />
                <Input
                    name="proposedRate"
                    placeholder="100"
                    onChange={onInputChange}
                    className="pkgCalc-form-number"
                    value={formData.proposedRate.toString()}
                    addonBefore={currency}
                />
            </FormRow>
            <LabelSelectRow
                onSelectChange={onSelectChange}
                selectValueName="numberOfNights"
                title="Number of Nights"
                selectStyles={selectStyles}
                selectSelectedOption={formData.numberOfNights}
            />
            <LabelSelectRow
                onSelectChange={onSelectChange}
                selectValueName="numberOfAdults"
                title="Number of Adults"
                selectStyles={selectStyles}
                selectSelectedOption={formData.numberOfAdults}
            />
            <LabelSelectRow
                onSelectChange={onSelectChange}
                selectValueName="numberOfChildren"
                title="Number of Children"
                selectStyles={selectStyles}
                selectSelectedOption={formData.numberOfChildren}
            />
            <LabelSelectRow
                onSelectChange={onSelectChange}
                selectValueName="numberOfInfants"
                title="Number of Infants"
                selectStyles={selectStyles}
                selectSelectedOption={formData.numberOfInfants}
            />
            <FormRow style={rowStyle}>
                <InputLabel text="VAT %" />
                <Input
                    name="vat"
                    placeholder="20"
                    onChange={onInputChange}
                    value={formData.vat.toString()}
                    className="pkgCalc-form-number"
                    addonAfter="%"
                />
                <OutputLabel text={`${currency}${formData.vatAmount.toFixed(2)}`} />
            </FormRow>
            <FormRow style={rowStyle}>
                <InputLabel text="Is this commissionable?" />
                <Toggle
                    name="commissionApplied"
                    checked={formData.commissionApplied}
                    onChange={(event) => commissionApplied(event)}
                    className="pkgCalc-form-toggle"
                />
            </FormRow>
            {formData.commissionApplied && (
                <FormRow style={rowStyle}>
                    <InputLabel text="Commission %" />
                    <Select
                        className="pkgCalc-form-select-commission"
                        name="commissionBrand"
                        onChange={onSelectChange}
                        styles={selectStyles}
                        options={options}
                        value={selectedCommissionOptionValue}
                    />
                    <Input
                        name="commissionPercentage"
                        placeholder="5%"
                        onChange={onInputChange}
                        value={formData.commissionPercentage.toString()}
                        className="pkgCalc-form-number"
                        style={{ marginLeft: '1em' }}
                        addonAfter="%"
                    />
                    <OutputLabel text={`${currency}${formData.commissionAmount.toFixed(2).toString()}`} />
                </FormRow>
            )}
        </div>
    );
};

export default Proposals;
